import React from "react";
import { Box, Typography, Button, Stack } from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import MarkdownRenderer from "../../common/components/MarkdownRenderer";
import { AccessTime, Paid, Sell, TipsAndUpdates } from "@mui/icons-material";

interface PositionCardProps {
  position: {
    displayNameOverride: string;
    type: string;
    description: string;
    isPartTime?: boolean;
    isFullTime?: boolean;
    receivesTips?: boolean;
    isSalary?: boolean;
    minSalaryCents?: number;
    maxSalaryCents?: number;
    isHourly?: boolean;
    minHourlyCents?: number;
    maxHourlyCents?: number;
  };
  companyName: string;
  companyAbout: string;
  onApplyNow: () => void;
  isEmployerView?: boolean;
}

const centsToDollars = (cents: number, decimals: number = 2) => {
  const dollars = (cents / 100).toFixed(decimals);
  const formatted = parseFloat(dollars).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return `$${formatted}`;
};

const formatEarnings = (position: PositionCardProps['position']) => {
  let salaryRange = null;
  let hourlyRange = null;
  if (position.isSalary) {
    if (position.minSalaryCents && position.maxSalaryCents) {
      salaryRange = centsToDollars(position.minSalaryCents, 0) + ' to ' + centsToDollars(position.maxSalaryCents, 0) + '/yr';
    } else if (position.minSalaryCents) {
      salaryRange = centsToDollars(position.minSalaryCents, 0) + '/yr';
    } else if (position.maxSalaryCents) {
      salaryRange = centsToDollars(position.maxSalaryCents, 0) + '/yr';
    }
  }

  if (position.isHourly) {
    if (position.minHourlyCents && position.maxHourlyCents) {
      hourlyRange = centsToDollars(position.minHourlyCents) + ' to ' + centsToDollars(position.maxHourlyCents) + '/hr';
    } else if (position.minHourlyCents) {
      hourlyRange = centsToDollars(position.minHourlyCents) + '/hr';
    } else if (position.maxHourlyCents) {
      hourlyRange = centsToDollars(position.maxHourlyCents) + '/hr';
    }
  }

  if (salaryRange && hourlyRange) {
    return salaryRange + ' or ' + hourlyRange;
  } else if (salaryRange) {
    return salaryRange;
  } else if (hourlyRange) {
    return hourlyRange;
  }

  return null;
};

const PositionCard: React.FC<PositionCardProps> = ({ 
  position, 
  companyName, 
  companyAbout, 
  onApplyNow,
  isEmployerView = false
}) => {
  const navigate = useNavigate();
  const { company, position: positionSlug } = useParams();

  const handleButtonClick = () => {
    if (isEmployerView) {
      // Navigate to edit page
      navigate(`/hiring/${company}/${positionSlug}/edit`);
    } else {
      // Apply now functionality
      onApplyNow();
    }
  };

  const earningsDisplay = formatEarnings(position);
  
  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: { xs: 4, md: 4 },
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        maxWidth: { xs: "600px", md: "none" },
      }}
    >
      <Stack spacing={2}>
        {/* Position Information */}
        <Stack spacing={2} sx={{pb: 4}}>
          {isEmployerView && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="plain"
                onClick={() => navigate(`/apply/${company}/${positionSlug}`)}
                sx={{
                  borderColor: "#2E2D7E",
                  color: "#2E2D7E",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#1a1a5e",
                    color: "#1a1a5e",
                    bgcolor: "rgba(46, 45, 126, 0.04)",
                  },
                }}
              >
                Applicant View
              </Button>

              <Button
                variant="outlined"
                onClick={handleButtonClick}
                sx={{
                  borderColor: "#2E2D7E",
                  color: "#2E2D7E",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#1a1a5e",
                    color: "#1a1a5e",
                    bgcolor: "rgba(46, 45, 126, 0.04)",
                  },
                }}
              >
                Edit Post
              </Button>
            </Stack>
          )}
            
          <Typography
            level="h2"
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 1,
            }}
          >
            {position.displayNameOverride}
          </Typography>
          
          <Typography
            startDecorator={<AccessTime />}
            level="body-lg"
            sx={{
              fontSize: { xs: "1rem", md: "1.125rem" },
              color: "#7B7F85",
              fontWeight: 500,
            }}
          >
            {position.isFullTime && position.isPartTime 
              ? 'Full-time or Part-time'
              : position.isFullTime 
                ? 'Full-time' 
                : position.isPartTime 
                  ? 'Part-time'
                  : position.type === 'full-time' 
                    ? 'Full-time' 
                    : 'Part-time'
            }
          </Typography>
          {position.receivesTips && (
            <Typography
              startDecorator={<Paid />}
              level="body-lg"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: "#7B7F85",
                fontWeight: 500,
                mt: 0.5,
              }}
            >
              {earningsDisplay}
              {earningsDisplay ? ' + tips' : 'Receives tips'}
            </Typography>
          )}

          {position.description && (  
            <MarkdownRenderer
              startDecorator={<Sell sx={{width: "24px", height: "24px"}} />}
              content={position.description}
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                mb: 4,
              }}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PositionCard; 